import { useEffect, useMemo, useState } from "react";

const INPUT_STYLE = {
  width: "100%",
  boxSizing: "border-box",
  padding: "9px 12px",
  border: "1.5px solid #e2e8f0",
  borderRadius: 8,
  fontSize: 14,
  color: "#1e293b",
  background: "#fff",
};

const PRIMARY_BUTTON_STYLE = {
  border: 0,
  borderRadius: 8,
  background: "#7c3aed",
  color: "#fff",
  padding: "8px 13px",
  fontWeight: 600,
  cursor: "pointer",
};

const SECONDARY_BUTTON_STYLE = {
  border: "1px solid #bfdbfe",
  borderRadius: 7,
  padding: "7px 11px",
  background: "#eff6ff",
  color: "#2563eb",
  fontWeight: 600,
  cursor: "pointer",
};

export const SCHEMA_DEFINITIONS = {
  Article: {
    fields: [
      { key: "headline", label: "Headline", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "author", label: "Author Name" },
      { key: "image", label: "Image URL", type: "url" },
      { key: "datePublished", label: "Published Date", type: "date" },
      { key: "dateModified", label: "Modified Date", type: "date" },
    ],
  },
  FAQPage: { fields: [] },
  Product: {
    fields: [
      { key: "name", label: "Product Name", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "image", label: "Image URL", type: "url" },
      { key: "price", label: "Price", type: "number" },
      { key: "priceCurrency", label: "Currency", placeholder: "e.g., INR" },
    ],
  },
  Organization: {
    fields: [
      { key: "name", label: "Organization Name", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "url", label: "Website URL", type: "url" },
      { key: "logo", label: "Logo URL", type: "url" },
    ],
  },
  LocalBusiness: {
    fields: [
      { key: "name", label: "Business Name", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "address", label: "Address", type: "textarea" },
      { key: "telephone", label: "Telephone" },
      { key: "url", label: "Website URL", type: "url" },
    ],
  },
  Person: {
    fields: [
      { key: "name", label: "Name", required: true },
      { key: "url", label: "Profile URL", type: "url" },
      { key: "image", label: "Image URL", type: "url" },
      { key: "jobTitle", label: "Job Title" },
    ],
  },
  Event: {
    fields: [
      { key: "name", label: "Event Name", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "startDate", label: "Start Date", type: "datetime" },
      { key: "endDate", label: "End Date", type: "datetime" },
      { key: "location", label: "Location" },
    ],
  },
  BreadcrumbList: { fields: [] },
  WebPage: {
    fields: [
      { key: "name", label: "Page Name", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "url", label: "Page URL", type: "url" },
    ],
  },
  Service: {
    fields: [
      { key: "name", label: "Service Name", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "provider", label: "Provider" },
      { key: "url", label: "Service URL", type: "url" },
    ],
  },
  Review: {
    fields: [
      { key: "name", label: "Reviewed Item", required: true },
      { key: "reviewBody", label: "Review", type: "textarea" },
      { key: "ratingValue", label: "Rating", type: "number" },
      { key: "author", label: "Author" },
    ],
  },
};

const EMPTY_FAQ = { question: "", answer: "" };
const EMPTY_BREADCRUMB = { name: "", url: "" };

function hasValue(value) {
  return typeof value === "string" ? value.trim().length > 0 : value !== "" && value !== null && value !== undefined;
}

function withSchemaContext(schema) {
  if (Array.isArray(schema)) {
    return schema.map(withSchemaContext);
  }

  return schema["@context"] ? schema : { "@context": "https://schema.org", ...schema };
}

function parseSchema(value) {
  const schema = JSON.parse(value);
  const entries = Array.isArray(schema) ? schema : [schema];

  if (!entries.length || !entries.every((entry) => entry && typeof entry === "object" && !Array.isArray(entry))) {
    throw new Error("JSON-LD must be an object or an array of objects.");
  }

  if (entries.some((entry) => Object.keys(entry).length === 0)) {
    throw new Error("A schema object cannot be empty.");
  }

  return withSchemaContext(schema);
}

function getSchemaLabel(schema) {
  if (schema?.["@type"]) return schema["@type"];
  if (schema?.type) return schema.type;
  return "Custom";
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 14, fontSize: 13, fontWeight: 600, color: "#374151" }}>
      {label}
      <div style={{ marginTop: 5, fontWeight: 400 }}>{children}</div>
    </label>
  );
}

function RepeaterRow({ children, onRemove }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 8 }}>
      {children}
      <button type="button" onClick={onRemove} style={{ border: 0, background: "none", color: "#dc2626", cursor: "pointer" }}>
        Remove
      </button>
    </div>
  );
}

export default function SchemaEditor({ schemas = [], onChange }) {
  const [mode, setMode] = useState("guided");
  const [schemaType, setSchemaType] = useState("Article");
  const [fieldValues, setFieldValues] = useState({});
  const [faqItems, setFaqItems] = useState([EMPTY_FAQ]);
  const [breadcrumbs, setBreadcrumbs] = useState([EMPTY_BREADCRUMB]);
  const [previewJson, setPreviewJson] = useState("");
  const [isPreviewEdited, setIsPreviewEdited] = useState(false);
  const [customJson, setCustomJson] = useState("");
  const [error, setError] = useState("");

  const definition = SCHEMA_DEFINITIONS[schemaType];
  const generatedSchema = useMemo(() => {
    const properties = Object.fromEntries(
      Object.entries(fieldValues).filter(([, value]) => hasValue(value)),
    );

    if (schemaType === "FAQPage") {
      properties.mainEntity = faqItems
        .filter(({ question, answer }) => hasValue(question) && hasValue(answer))
        .map(({ question, answer }) => ({
          "@type": "Question",
          name: question.trim(),
          acceptedAnswer: { "@type": "Answer", text: answer.trim() },
        }));
    }

    if (schemaType === "BreadcrumbList") {
      properties.itemListElement = breadcrumbs
        .filter(({ name, url }) => hasValue(name) && hasValue(url))
        .map(({ name, url }, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: name.trim(),
          item: url.trim(),
        }));
    }

    return { "@context": "https://schema.org", "@type": schemaType, ...properties };
  }, [breadcrumbs, faqItems, fieldValues, schemaType]);

  const generatedPreview = useMemo(() => JSON.stringify(generatedSchema, null, 2), [generatedSchema]);

  useEffect(() => {
    if (!isPreviewEdited) setPreviewJson(generatedPreview);
  }, [generatedPreview, isPreviewEdited]);

  const resetGuidedEditor = () => {
    setFieldValues({});
    setFaqItems([EMPTY_FAQ]);
    setBreadcrumbs([EMPTY_BREADCRUMB]);
    setIsPreviewEdited(false);
    setError("");
  };

  const updateRepeater = (setter, index, key, value) => {
    setter((items) => items.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item)));
  };

  const validateGuidedSchema = () => {
    const missingRequiredField = definition.fields.find((field) => field.required && !hasValue(fieldValues[field.key]));
    if (missingRequiredField) return `${missingRequiredField.label} is required.`;
    if (schemaType === "FAQPage" && !faqItems.some(({ question, answer }) => hasValue(question) && hasValue(answer))) {
      return "Add at least one complete question and answer.";
    }
    if (schemaType === "BreadcrumbList" && !breadcrumbs.some(({ name, url }) => hasValue(name) && hasValue(url))) {
      return "Add at least one complete breadcrumb item.";
    }
    return "";
  };

  const addSchema = (source, validateGuided = false) => {
    const guidedError = validateGuided ? validateGuidedSchema() : "";
    if (guidedError) {
      setError(guidedError);
      return;
    }

    try {
      const parsedSchema = parseSchema(source);
      const entries = Array.isArray(parsedSchema) ? parsedSchema : [parsedSchema];

      if (validateGuided) {
        const missingPreviewField = definition.fields
          .filter((field) => field.required)
          .find((field) => !hasValue(entries[0]?.[field.key]));

        if (missingPreviewField) {
          throw new Error(`${missingPreviewField.label} is required in the JSON-LD preview.`);
        }
        const isIncompleteFaq = schemaType === "FAQPage" && !Array.isArray(entries[0]?.mainEntity);
        const isIncompleteBreadcrumbs = schemaType === "BreadcrumbList" && !Array.isArray(entries[0]?.itemListElement);
        if (isIncompleteFaq || isIncompleteBreadcrumbs) {
          throw new Error(`The ${schemaType} JSON-LD preview is incomplete.`);
        }
      }

      onChange([...schemas, ...entries]);
      setError("");
      if (validateGuided) resetGuidedEditor();
      else setCustomJson("");
    } catch (parseError) {
      setError(`Invalid JSON-LD: ${parseError.message}`);
    }
  };

  return (
    <div>
      <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, background: "#f8fafc", padding: 12, marginBottom: 16 }}>
        <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 600 }}>Current Schemas</div>
        {schemas.length ? (
          schemas.map((schema, index) => (
            <div key={index} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: index < schemas.length - 1 ? "1px solid #e2e8f0" : 0 }}>
              <span style={{ fontSize: 13 }}><b>{getSchemaLabel(schema)} Schema</b></span>
              <button type="button" onClick={() => onChange(schemas.filter((_, itemIndex) => itemIndex !== index))} style={{ border: 0, background: "none", color: "#dc2626", cursor: "pointer" }}>
                Remove
              </button>
            </div>
          ))
        ) : (
          <em style={{ fontSize: 13, color: "#64748b" }}>No schemas added yet.</em>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button type="button" onClick={() => { setMode("guided"); setError(""); }} style={mode === "guided" ? PRIMARY_BUTTON_STYLE : SECONDARY_BUTTON_STYLE}>
          Guided Generator
        </button>
        <button type="button" onClick={() => { setMode("custom"); setError(""); }} style={mode === "custom" ? PRIMARY_BUTTON_STYLE : SECONDARY_BUTTON_STYLE}>
          Custom JSON-LD
        </button>
      </div>

      {mode === "guided" ? (
        <>
          <Field label="Schema Type">
            <select
              style={INPUT_STYLE}
              value={schemaType}
              onChange={(event) => { setSchemaType(event.target.value); resetGuidedEditor(); }}
            >
              {Object.keys(SCHEMA_DEFINITIONS).map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
          </Field>

          {definition.fields.map((field) => (
            <Field key={field.key} label={`${field.label}${field.required ? " *" : ""}`}>
              {field.type === "textarea" ? (
                <textarea rows="3" style={INPUT_STYLE} placeholder={field.placeholder} value={fieldValues[field.key] || ""} onChange={(event) => { setFieldValues((values) => ({ ...values, [field.key]: event.target.value })); setIsPreviewEdited(false); }} />
              ) : (
                <input type={field.type || "text"} style={INPUT_STYLE} placeholder={field.placeholder} value={fieldValues[field.key] || ""} onChange={(event) => { setFieldValues((values) => ({ ...values, [field.key]: event.target.value })); setIsPreviewEdited(false); }} />
              )}
            </Field>
          ))}

          {schemaType === "FAQPage" && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 600 }}>Questions and Answers</div>
              {faqItems.map((item, index) => (
                <RepeaterRow key={index} onRemove={() => { setFaqItems((items) => items.filter((_, itemIndex) => itemIndex !== index)); setIsPreviewEdited(false); }}>
                  <input style={INPUT_STYLE} placeholder="Question" value={item.question} onChange={(event) => { updateRepeater(setFaqItems, index, "question", event.target.value); setIsPreviewEdited(false); }} />
                  <input style={INPUT_STYLE} placeholder="Answer" value={item.answer} onChange={(event) => { updateRepeater(setFaqItems, index, "answer", event.target.value); setIsPreviewEdited(false); }} />
                </RepeaterRow>
              ))}
              <button type="button" onClick={() => { setFaqItems((items) => [...items, EMPTY_FAQ]); setIsPreviewEdited(false); }} style={SECONDARY_BUTTON_STYLE}>+ Add Question</button>
            </div>
          )}

          {schemaType === "BreadcrumbList" && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 600 }}>Breadcrumb Items</div>
              {breadcrumbs.map((item, index) => (
                <RepeaterRow key={index} onRemove={() => { setBreadcrumbs((items) => items.filter((_, itemIndex) => itemIndex !== index)); setIsPreviewEdited(false); }}>
                  <input style={INPUT_STYLE} placeholder="Breadcrumb name" value={item.name} onChange={(event) => { updateRepeater(setBreadcrumbs, index, "name", event.target.value); setIsPreviewEdited(false); }} />
                  <input style={INPUT_STYLE} placeholder="URL" value={item.url} onChange={(event) => { updateRepeater(setBreadcrumbs, index, "url", event.target.value); setIsPreviewEdited(false); }} />
                </RepeaterRow>
              ))}
              <button type="button" onClick={() => { setBreadcrumbs((items) => [...items, EMPTY_BREADCRUMB]); setIsPreviewEdited(false); }} style={SECONDARY_BUTTON_STYLE}>+ Add Breadcrumb</button>
            </div>
          )}

          <Field label="Generated JSON-LD Preview">
            <textarea rows="12" style={{ ...INPUT_STYLE, fontFamily: "monospace" }} value={previewJson} onChange={(event) => { setPreviewJson(event.target.value); setIsPreviewEdited(true); }} />
          </Field>
          <button type="button" onClick={() => addSchema(previewJson, true)} style={PRIMARY_BUTTON_STYLE}>+ Add Schema</button>
        </>
      ) : (
        <>
          <Field label="Custom JSON-LD">
            <textarea rows="12" style={{ ...INPUT_STYLE, fontFamily: "monospace" }} value={customJson} onChange={(event) => setCustomJson(event.target.value)} placeholder={'{\n  "@context": "https://schema.org",\n  "@type": "Organization"\n}'} />
          </Field>
          <button type="button" onClick={() => addSchema(customJson)} style={PRIMARY_BUTTON_STYLE}>+ Add Schema</button>
        </>
      )}

      {error && <p style={{ margin: "12px 0 0", color: "#dc2626", fontSize: 13 }}>{error}</p>}
    </div>
  );
}
